import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import { formatDate } from '../lib/format.js';
import {
  Button,
  Card,
  CardHead,
  Dash,
  EmptyState,
  ErrorBlock,
  Loading,
  PageHeader,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal, Confirm } from '../components/Modal.js';
import { Field, TextInput } from '../components/Field.js';
import { useToast } from '../components/Toast.js';
import { IconOptOut, IconPlus, IconTrash } from '../components/icons.js';
import type { OptOut } from '../../data/port.js';

export function Optout() {
  const { data } = useApp();
  const toast = useToast();
  const podeEditar = useCan('console-operador');
  const [adicionar, setAdicionar] = useState(false);
  const [remover, setRemover] = useState<OptOut | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const estado = useAsync<OptOut[]>(() => data.listOptOuts(), [data]);

  const confirmarRemocao = async (registro: OptOut) => {
    setOcupado(true);
    try {
      const ok = await data.removeOptOut(registro.taxId);
      if (ok) {
        toast.success(`${registro.taxId} volta a receber cobrança`);
        estado.reload();
      } else {
        toast.error(`${registro.taxId} não estava em opt-out`);
      }
      setRemover(null);
    } finally {
      setOcupado(false);
    }
  };

  const colunas: Column<OptOut>[] = [
    { key: 'doc', header: 'CPF/CNPJ', cell: (o) => <span className="num td--strong">{o.taxId}</span> },
    { key: 'motivo', header: 'Motivo', cell: (o) => o.reason ?? <Dash /> },
    { key: 'desde', header: 'Desde', cell: (o) => <span className="num">{formatDate(o.createdAt) ?? o.createdAt}</span> },
    ...(podeEditar
      ? [
          {
            key: 'acao',
            header: '',
            align: 'right' as const,
            cell: (o: OptOut) => (
              <Button variant="danger-ghost" size="sm" onClick={() => setRemover(o)}>
                <IconTrash size={14} /> Remover
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Opt-out"
        desc="Documentos que pediram para não receber cobrança."
        actions={
          podeEditar && (
            <Button variant="primary" onClick={() => setAdicionar(true)}>
              <IconPlus size={16} /> Adicionar
            </Button>
          )
        }
      />

      <Card>
        <CardHead title="Em opt-out" sub={estado.data ? `${estado.data.length} documento(s)` : undefined} />
        <div className="card__body--flush">
          {estado.loading && !estado.data && <Loading />}
          {estado.error && <ErrorBlock message={estado.error} />}
          {estado.data && estado.data.length === 0 && (
            <EmptyState
              icon={<IconOptOut size={40} />}
              title="Ninguém em opt-out"
              desc="Todos os clientes estão elegíveis a receber cobrança."
            />
          )}
          {estado.data && estado.data.length > 0 && (
            <DataTable columns={colunas} rows={estado.data} keyOf={(o) => o.taxId} />
          )}
        </div>
      </Card>

      {adicionar && (
        <AdicionarOptOut
          onClose={() => setAdicionar(false)}
          onAdicionado={() => {
            setAdicionar(false);
            estado.reload();
          }}
        />
      )}
      {remover && (
        <Confirm
          title="Remover do opt-out"
          confirmLabel="Remover"
          busy={ocupado}
          message={`${remover.taxId} volta a receber cobrança. Confirma?`}
          onConfirm={() => void confirmarRemocao(remover)}
          onClose={() => setRemover(null)}
        />
      )}
    </>
  );
}

function AdicionarOptOut({ onClose, onAdicionado }: { onClose: () => void; onAdicionado: () => void }) {
  const { data } = useApp();
  const toast = useToast();
  const [documento, setDocumento] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const adicionar = async () => {
    if (!documento.trim()) {
      setErro('Informe o CPF/CNPJ.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const ok = await data.addOptOut(documento.trim(), motivo.trim() || undefined);
      if (ok) {
        toast.success(`${documento.trim()} não receberá mais cobrança`);
        onAdicionado();
      } else {
        setErro(`${documento.trim()} já estava em opt-out.`);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao adicionar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      title="Adicionar ao opt-out"
      sub="O cliente deixa de receber qualquer cobrança."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>Cancelar</Button>
          <Button variant="primary" onClick={() => void adicionar()} loading={salvando}>Adicionar</Button>
        </>
      }
    >
      {erro && <div className="notice notice--danger">{erro}</div>}
      <div className="form-grid">
        <Field label="CPF/CNPJ" required hint="com ou sem máscara">
          <TextInput value={documento} onChange={setDocumento} mono autoFocus placeholder="248.386.290-01" />
        </Field>
        <Field label="Motivo" hint="opcional">
          <TextInput value={motivo} onChange={setMotivo} placeholder="pediu no whatsapp" onEnter={() => void adicionar()} />
        </Field>
      </div>
    </Modal>
  );
}
